import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/Button";
import { CardTarefa } from "./CardTarefa";
import type { TarefaDto, TarefaStatus } from "@/services/tarefas";
import {
  addDaysToKey,
  dayNumber,
  formatFullWeekRange,
  formatWeekdayShort,
  getFullWeekDays,
  getWeekStartFromKey,
  horarioToMinutes,
  todayKey,
} from "./dateUtils";
import { cardColor } from "./grupoColors";
import { useNowMinutes } from "./useNowMinutes";

const HOUR_START = 6;
const HOUR_END = 22;
const HOUR_HEIGHT = 56;
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

interface CalendarioSemanalGridProps {
  weekStart: string;
  eventos: TarefaDto[];
  currentUserId: string;
  busyId?: string | null;
  onWeekChange: (weekStart: string) => void;
  onNovaTarefa: (data: string) => void;
  onEdit: (tarefa: TarefaDto) => void;
  onLeadtime: (tarefa: TarefaDto) => void;
  onStatusChange: (tarefa: TarefaDto, status: TarefaStatus) => void;
}

export function CalendarioSemanalGrid({
  weekStart,
  eventos,
  currentUserId,
  busyId,
  onWeekChange,
  onNovaTarefa,
  onEdit,
  onLeadtime,
  onStatusChange,
}: CalendarioSemanalGridProps) {
  const today = todayKey();
  const weekDays = useMemo(() => getFullWeekDays(weekStart), [weekStart]);
  const nowMinutes = useNowMinutes();
  const gridHeight = HOURS.length * HOUR_HEIGHT;

  const byDay = useMemo(() => {
    const map = new Map<string, { allDay: TarefaDto[]; timed: TarefaDto[] }>();
    for (const day of weekDays) map.set(day, { allDay: [], timed: [] });
    for (const ev of eventos) {
      if (!map.has(ev.data)) continue;
      const b = map.get(ev.data)!;
      if (horarioToMinutes(ev.horario) != null) b.timed.push(ev);
      else b.allDay.push(ev);
    }
    for (const [, b] of map) {
      b.timed.sort((a, b) => (a.horario ?? "").localeCompare(b.horario ?? ""));
    }
    return map;
  }, [eventos, weekDays]);

  const goPrev = () => onWeekChange(addDaysToKey(weekStart, -7));
  const goNext = () => onWeekChange(addDaysToKey(weekStart, 7));
  const goToday = () => onWeekChange(getWeekStartFromKey(todayKey()));

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="flex flex-shrink-0 flex-wrap items-center gap-1 border-b border-slate-200 px-3 py-2 sm:px-4">
        <Button variant="ghost" size="sm" onClick={goPrev} aria-label="Semana anterior">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={goNext} aria-label="Próxima semana">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={goToday}>
          Hoje
        </Button>
        <h2 className="ml-1 min-w-0 flex-1 truncate text-sm font-medium text-slate-800 sm:ml-2 sm:text-base">
          {formatFullWeekRange(weekStart)}
        </h2>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="min-w-[36rem] sm:min-w-[48rem] lg:min-w-[56rem] xl:min-w-[900px]">
          {/* Cabeçalho dias */}
          <div className="sticky top-0 z-20 flex border-b border-slate-200 bg-white">
            <div className="w-14 flex-shrink-0" />
            {weekDays.map((dayKey) => {
              const isToday = dayKey === today;
              return (
                <div
                  key={dayKey}
                  className="flex flex-1 flex-col items-center border-l border-slate-100 py-2"
                >
                  <span className="text-[10px] font-semibold uppercase text-slate-500">
                    {formatWeekdayShort(dayKey)}
                  </span>
                  <span
                    className={cn(
                      "mt-0.5 flex h-8 w-8 items-center justify-center rounded-full text-base font-medium",
                      isToday ? "bg-blue-600 text-white" : "text-slate-700",
                    )}
                  >
                    {dayNumber(dayKey)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Dia inteiro */}
          <div className="flex border-b border-slate-200">
            <div className="w-14 flex-shrink-0 pt-2 pr-1 text-right text-[10px] text-slate-400">
              Dia inteiro
            </div>
            {weekDays.map((dayKey) => {
              const allDay = byDay.get(dayKey)?.allDay ?? [];
              return (
                <div
                  key={dayKey}
                  className="min-h-[48px] flex-1 space-y-1.5 border-l border-slate-100 p-1.5"
                >
                  {allDay.map((ev) => (
                    <CardTarefa
                      key={ev.id}
                      tarefa={ev}
                      currentUserId={currentUserId}
                      busy={busyId === ev.id}
                      onEdit={onEdit}
                      onLeadtime={onLeadtime}
                      onStatusChange={onStatusChange}
                    />
                  ))}
                  {allDay.length === 0 && (
                    <button
                      type="button"
                      onClick={() => onNovaTarefa(dayKey)}
                      className="w-full py-2 text-center text-[10px] text-slate-300 hover:text-violet-500"
                    >
                      + evento
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Grade horária */}
          <div className="relative flex">
            <div className="w-14 flex-shrink-0">
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="relative border-t border-slate-100"
                  style={{ height: HOUR_HEIGHT }}
                >
                  <span className="absolute -top-2.5 right-1 text-[10px] text-slate-400">
                    {String(h).padStart(2, "0")}:00
                  </span>
                </div>
              ))}
            </div>

            {weekDays.map((dayKey) => {
              const timed = byDay.get(dayKey)?.timed ?? [];
              const isToday = dayKey === today;
              const showNow =
                isToday &&
                nowMinutes >= HOUR_START * 60 &&
                nowMinutes < HOUR_END * 60;
              const nowTop = ((nowMinutes - HOUR_START * 60) / 60) * HOUR_HEIGHT;

              return (
                <div
                  key={dayKey}
                  className="relative flex-1 border-l border-slate-100"
                  style={{ height: gridHeight }}
                >
                  {HOURS.map((h) => (
                    <div
                      key={h}
                      className="border-t border-slate-100"
                      style={{ height: HOUR_HEIGHT }}
                    />
                  ))}

                  {showNow && (
                    <div
                      className="pointer-events-none absolute left-0 right-0 z-20 flex items-center"
                      style={{ top: nowTop }}
                    >
                      <div className="h-2.5 w-2.5 -ml-[5px] rounded-full bg-red-500 shadow" />
                      <div className="h-[2px] flex-1 bg-red-500" />
                    </div>
                  )}

                  {timed.map((ev) => {
                    const start = horarioToMinutes(ev.horario);
                    if (start == null) return null;
                    const end = horarioToMinutes(ev.horarioFim) ?? start + 60;
                    const top = ((start - HOUR_START * 60) / 60) * HOUR_HEIGHT;
                    const height = Math.max(((end - start) / 60) * HOUR_HEIGHT, 28);
                    const color = cardColor(ev);

                    return (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={() => onEdit(ev)}
                        className={cn(
                          "absolute left-1 right-1 z-10 overflow-hidden rounded-md px-2 py-1 text-left shadow-sm",
                          color.block,
                          ev.status === "concluida" && "opacity-60 line-through",
                        )}
                        style={{ top, height }}
                      >
                        <p className="whitespace-normal break-words text-[11px] font-semibold leading-tight">
                          {ev.titulo}
                        </p>
                        <p className="mt-0.5 text-[10px] opacity-90">
                          {ev.horario}
                          {ev.horarioFim ? ` – ${ev.horarioFim}` : ""}
                        </p>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
